import React, { useEffect, useLayoutEffect, useRef } from "react";
import { menu } from "./sidebarLinks";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import Header from "../Header/Header";
import "./sideNavbar.css";
import { ChevronsLeft } from "lucide-react";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import { cn } from "../../../lib/utils";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUtilsWhole,
  toggleSidenav,
} from "../../../redux/features/toolsSlice";
import useScreenResolution from "../../../hooks/useScreenResolution";
import MobileFooter from "./mobileFooter";
import "./sideNavbar.css";
import { selectProfileData } from "../../../redux/features/profileSlice";
import { selectLoginTokenInfo } from "../../../redux/features/loginRegisterSlice";
import Gleap from "gleap";
// The rail's two widths, in one place. The edge handle is positioned from the
// same numbers, so the two cannot drift apart and leave the handle floating off
// the border. The handle straddles the edge, hence the half-size offset.
const RAIL_WIDTH_OPEN = 256;
const RAIL_WIDTH_COLLAPSED = 72;
const EDGE_HANDLE_SIZE = 28;

const SideNavbar = ({ isPopup = false, invalidPage }) => {
  const screenResolution = useScreenResolution();
  const translate = useLanguageTranslate();
  const dispatch = useDispatch();
  const sidenavOpen = useSelector(selectUtilsWhole).sidenav_open;
  const profileData = useSelector(selectProfileData);
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const { pathname } = useLocation();

  let hideFooterRoutes = [
    "/sign-in",
    "/forgot-password",
    "/sign-up",
    "/life-insurance",
    "/motor-insurance/cancel-policy",
    "/motor-insurance/claim-details",
    "/motor-insurance/upload-documents",
    "motor-insurance/change-garage",
    "/motor-insurance/payment-details",
    "/motor-insurance/success",
    "/motor-insurance/claim-details/claim-info",
    "/change-email",
    "/change-mobile-number",
    "/motor-insurance/reason-for-rejection",
    "/motor-insurance/file-new-claim",
    "/motor-insurance/add-more-covers",
    "/travel-insurance", // Hide footer for all travel insurance flows
    "/domestic-helper-insurance", // Hide footer for all domestic helper flows
    "/home-insurance", // Hide footer for all home insurance flows
    "/personal-accident-insurance", // Hide footer for all personal accident flows
    "/personal-cyber-insurance", // Hide footer for all personal cyber flows
  ];

  // Whether the fixed rail is on screen at all. Below 768px it is replaced by
  // the mobile footer, and popup layouts have no chrome.
  const showSidebar = !isPopup && !screenResolution.mobile;
  const railRef = useRef(null);

  /**
   * Publishes the rail's real width as `--sidenav-width` for the layout to pad
   * the page content by.
   *
   * The offset used to be a pair of breakpoint guesses — `sm:pl-64` with an
   * `ipad:pl-20` override — and neither matched what the rail actually is. The
   * collapsed rail is `w-max`: about 92px on an iPad and 108px on a desktop,
   * against an assumed 80px, so content sat underneath it. The RTL side never
   * got the iPad override at all, so an Arabic iPad was out by 176px.
   *
   * Worse, the guesses keyed off the viewport while the rail's width keys off
   * `sidenav_open`. Collapsing the rail on an iPad in landscape left a 256px
   * gutter beside a 108px rail, and rotating the device changed the padding
   * without changing the rail.
   *
   * Measuring removes the guess: one number, always the truth, and the
   * ResizeObserver keeps it in step through the rail's own width transition.
   */
  useLayoutEffect(() => {
    const root = document.documentElement;
    const node = railRef.current;

    if (!showSidebar || !node) {
      root.style.setProperty("--sidenav-width", "0px");
      return undefined;
    }

    const publish = () =>
      root.style.setProperty("--sidenav-width", `${node.offsetWidth}px`);

    publish();

    // Guarded: jsdom and older Safari have no ResizeObserver, and the initial
    // publish above is still correct for them — only the open/close transition
    // stops being followed.
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    return () => observer.disconnect();
  }, [showSidebar, sidenavOpen]);

  // The rail is fixed-position chrome, so it must not leave a stale offset
  // behind on a layout that does not render it.
  useEffect(
    () => () => document.documentElement.style.setProperty("--sidenav-width", "0px"),
    []
  );

  const linkClickHandler = (data, event) => {
    if (data?.is_internal == false) {
      event.preventDefault();
      window.open(data.link, "_blank");
    }
    if (data.actionName == "GleapChat") {
      event.preventDefault();
      Gleap.open();
    }
  };

  return (
    <>
      {(!isPopup || !screenResolution.mobile) && <Header isPopup={isPopup} invalidPage={invalidPage} />}
      <Tooltip id="sideNav-tooltip" place="left-start" />
      {showSidebar ? (
        <aside
          className="hidden w-52 pt-12 text-black fixed inset-y-0 overflow-x-hidden overflow-y-auto sm:block z-10 ipad:w-auto rtl:right-0"
        >
          {/* Two explicit widths rather than `w-max`, because a width the
              browser derives from content cannot be transitioned — which is
              why collapsing used to snap. `overflow-x-hidden` lets the labels
              be clipped by the closing panel instead of reflowing, and the
              transition is scoped to `width` so the browser is not asked to
              interpolate every property on a container this large. */}
          <div
            ref={railRef}
            className={cn(
              // `inset-y-0` + `pt-12` rather than `h-full` off a static top:
              // the rail used to start 48px down and still claim 100vh, so it
              // ran 48px past the bottom of the screen. Anything anchored to
              // its foot — like the collapse control below — fell off the
              // viewport. `start-0` puts it on the correct edge in both
              // reading directions without a second RTL rule.
              "side-bar fixed inset-y-0 start-0 flex flex-col overflow-x-hidden border-r bg-[#f6f8f9] pt-12 rtl:border-l rtl:border-r-0",
              "transition-[width] duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)] motion-reduce:transition-none"
            )}
            style={{
              width: sidenavOpen ? RAIL_WIDTH_OPEN : RAIL_WIDTH_COLLAPSED,
            }}
          >
            <div
              id="side-navigation"
              className="overflow-y-auto overflow-x-hidden flex-grow"
            >
              {/* `px-4` at every width — the old `ipad:p-2` override made the
                  collapsed rail's padding different on an iPad than on a
                  desktop, so the icons could not be centred for both. */}
              <ul className="flex flex-col space-y-1 px-4 py-6 sidebar">
                {menu.map((section, index) => (
                  <div key={index} className=" pt-0 pb-4 px-0 border-b li-box">
                    <li className="px-5">
                      <div className="flex flex-row items-center ">
                        {/* Always rendered, faded rather than unmounted. When
                            these were conditional the group headings vanished
                            from the flow and every menu item below jumped up
                            the moment you collapsed — the rail changed height
                            and width at once. Now only the ink changes. */}
                        <div
                          className={cn(
                            "menu-subtitle pt-4 pb-2 mb-2 text-[#84919A] text-xs not-italic font-semibold leading-4 tracking-[0.12px] uppercase whitespace-nowrap rtl:pr-1",
                            "transition-opacity duration-200 motion-reduce:transition-none",
                            sidenavOpen ? "opacity-100" : "opacity-0"
                          )}
                          aria-hidden={!sidenavOpen}
                        >
                          {translate(section.title_en, section.title_ar)}
                        </div>
                      </div>
                    </li>
                    {section.links.map(
                      (link, linkIndex) =>
                        !link.hide && (
                          <li
                            key={linkIndex}
                            className={`${
                              link.showIfIsLoggedIn == true && !loginTokenInfo
                                ? "hidden"
                                : ""
                            } ${
                              pathname == link.link ||
                              (link.type == "policy" &&
                                pathname.includes(
                                  "/life-insurance/policy-overview"
                                )) ||
                              (link.type == "policy" &&
                                pathname.includes(
                                  "/motor-insurance/policy-overview"
                                ))
                                ? "active"
                                : "inActive"
                            }`}
                            data-tooltip-id="sideNav-tooltip"
                            data-tooltip-content={
                              !sidenavOpen
                                ? translate(link.text_en, link.text_ar)
                                : ""
                            }
                          >
                            <Link
                              id={link?.actionName}
                              onClick={(event) => linkClickHandler(link, event)}
                              to={link.link}
                              state={{ navigatedFromLink: link.text }}
                              // No leading border. A `border-l-4
                              // border-transparent` used to sit here with
                              // nothing ever colouring it — the active state is
                              // a filled pill on the <li> — and those 4px came
                              // out of the icon column, landing every icon 4px
                              // right of centre in the 72px collapsed rail.
                              // An active bar, if one is ever wanted, belongs
                              // on this anchor's ::before: it is already
                              // `relative`, and an absolute indicator costs the
                              // row no width.
                              className={`relative flex flex-row items-center h-11 focus:outline-none hover:bg-[#eef0f2]  hover:rounded-md text-[#252c32] text-sm not-italic font-normal leading-6 ${
                                link.disabled
                                  ? "pointer-events-none opacity-50"
                                  : ""
                              }`}
                            >
                              {/* A fixed 40px column, which is exactly the
                                  collapsed rail (72px) minus its 16px padding
                                  on each side. Centring the icon in it leaves
                                  24px of clear space on both sides when
                                  collapsed, instead of the 28/20 split a
                                  leading margin gave. It also keeps the icon at
                                  the same x while the panel opens, so the rail
                                  reads as widening around the icons rather
                                  than dragging them along. */}
                              <span className="inline-flex w-10 shrink-0 items-center justify-center">
                                <img
                                  src={link.icon}
                                  alt=""
                                  aria-hidden="true"
                                />
                              </span>
                              <span
                                className={cn(
                                  "ms-2 whitespace-nowrap text-[#252C32] text-sm not-italic font-normal leading-6 tracking-wide",
                                  "transition-opacity duration-200 motion-reduce:transition-none",
                                  sidenavOpen ? "opacity-100" : "opacity-0"
                                )}
                              >
                                {translate(link.text_en, link.text_ar)}
                              </span>
                            </Link>
                          </li>
                        )
                    )}
                  </div>
                ))}
              </ul>
            </div>
          </div>

          {/* The collapse handle, straddling the panel's outer edge.

              It is a sibling of the rail, not a child, because the rail has to
              clip its own overflow — that is what lets the labels be cut off by
              the closing panel instead of reflowing — and a child sitting half
              outside the border would be sliced down the middle by it. Being
              `fixed`, it escapes the aside's clipping too.

              It travels with the edge under its own transition rather than
              being driven by the measured `--sidenav-width`: same duration and
              same curve as the rail's width, so the two move as one object.
              Reading the observed width instead would leave it a frame behind
              and make it look like it was dragging along after the panel. */}
          {!invalidPage && (
            <button
              type="button"
              onClick={() => dispatch(toggleSidenav())}
              aria-expanded={sidenavOpen}
              aria-controls="side-navigation"
              aria-label={
                sidenavOpen
                  ? translate("Collapse menu", "طي القائمة")
                  : translate("Expand menu", "توسيع القائمة")
              }
              data-tooltip-id="sideNav-tooltip"
              data-tooltip-content={
                sidenavOpen
                  ? translate("Collapse menu", "طي القائمة")
                  : translate("Expand menu", "توسيع القائمة")
              }
              style={{
                insetInlineStart:
                  (sidenavOpen ? RAIL_WIDTH_OPEN : RAIL_WIDTH_COLLAPSED) -
                  EDGE_HANDLE_SIZE / 2,
                width: EDGE_HANDLE_SIZE,
                height: EDGE_HANDLE_SIZE,
              }}
              className={cn(
                "fixed top-16 z-30 flex items-center justify-center rounded-full",
                "border border-[#DDE2E4] bg-white text-[#5B6871] shadow-[0_1px_3px_rgba(26,32,36,0.14)]",
                "hover:border-[#B8C4CB] hover:bg-[#F6F8F9] hover:text-[#1A2024]",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A9E7] focus-visible:ring-offset-2",
                "transition-[inset-inline-start,background-color,border-color,color] duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)] motion-reduce:transition-none"
              )}
            >
              {/* One glyph that turns, rather than two panel icons swapping
                  places. `rotate-180` points it the other way when collapsed;
                  `-scale-x-100` mirrors it for Arabic, where the rail is on the
                  other edge. The two compose — Tailwind writes both into the
                  same transform — and the rotation is timed to the panel, so
                  the arrow turns as the rail slides rather than snapping when
                  it lands. */}
              <ChevronsLeft
                className={cn(
                  "size-4 transition-transform duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)] motion-reduce:transition-none rtl:-scale-x-100",
                  !sidenavOpen && "rotate-180"
                )}
                strokeWidth={2.25}
                aria-hidden="true"
              />
            </button>
          )}
        </aside>
      ) : null}

      {screenResolution.mobile &&
        !hideFooterRoutes?.some((path) => pathname?.includes(path)) && (
          <MobileFooter />
        )}
    </>
  );
};

export default SideNavbar;
